import React, { useState, useRef } from 'react';
import { useVideoUpload } from '../../context/VideoUploadContext';
import './UploadVideoModal.scss';

export const UploadVideoModal = ({ isOpen, onClose, onSuccess }) => {
  const { startUpload } = useVideoUpload();

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [error, setError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const fileInputRef = useRef(null);

  const [videoDuration, setVideoDuration] = useState(null);

  if (!isOpen) return null;

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const handleFileChange = (file) => {
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      setError('Please select a valid video file (e.g. MP4, MOV, WEBM).');
      return;
    }

    // 700 MB size check
    const MAX_SIZE = 700 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      setError('File size exceeds the maximum allowed limit of 700 MB.');
      return;
    }

    // 2 minutes (120 seconds) video duration validation
    const tempVideo = document.createElement('video');
    tempVideo.preload = 'metadata';
    const tempUrl = URL.createObjectURL(file);
    tempVideo.src = tempUrl;

    tempVideo.onloadedmetadata = () => {
      URL.revokeObjectURL(tempUrl);
      const duration = tempVideo.duration;
      const MAX_DURATION_SECONDS = 120; // 2 minutes limit

      if (duration > MAX_DURATION_SECONDS) {
        const mins = Math.floor(duration / 60);
        const secs = Math.floor(duration % 60);
        setError(`Video duration exceeds the maximum allowed limit of 2 minutes (Selected video length: ${mins}m ${secs}s). Please choose a video under 2 minutes.`);
        return;
      }

      setError(null);
      setSelectedFile(file);
      setVideoDuration(duration);

      // Create video preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);

      // Auto-fill title with filename without extension if title is empty
      if (!title) {
        const fileNameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
        setTitle(fileNameWithoutExt);
      }
    };

    tempVideo.onerror = () => {
      URL.revokeObjectURL(tempUrl);
      setError(null);
      setSelectedFile(file);
      setVideoDuration(null);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      if (!title) {
        const fileNameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
        setTitle(fileNameWithoutExt);
      }
    };
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setVideoDuration(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedFile) {
      setError('Please select a video file to upload.');
      return;
    }

    if (!title.trim()) {
      setError('Title is required.');
      return;
    }

    // Start background upload — modal closes immediately
    startUpload({
      file: selectedFile,
      title,
      description,
      tags,
      onSuccess,
    });

    // Reset local form state
    setSelectedFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setTitle('');
    setDescription('');
    setTags('');
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';

    // Close modal immediately — upload continues in background
    onClose();
  };

  const handleModalClose = () => {
    handleRemoveFile();
    setTitle('');
    setDescription('');
    setTags('');
    setError(null);
    onClose();
  };

  return (
    <div className="uv-modal-backdrop" onClick={handleModalClose}>
      <div className="uv-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="uv-modal-header">
          <div className="uv-title-group">
            <i className="bi bi-cloud-arrow-up-fill uv-header-icon" />
            <h3>Upload Video</h3>
          </div>
          <button className="uv-close-btn" onClick={handleModalClose} aria-label="Close">
            <i className="bi bi-x-lg" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="uv-modal-body">
          {error && (
            <div className="uv-alert uv-alert-danger">
              <i className="bi bi-exclamation-triangle-fill" /> {error}
            </div>
          )}

          {/* Background Upload Info Banner */}
          <div className="uv-info-banner">
            <i className="bi bi-info-circle-fill" />
            <span>Upload runs in the background — you can browse the site while it processes!</span>
          </div>

          {/* Video Dropzone / Preview */}
          {!selectedFile ? (
            <div
              className={`uv-dropzone ${isDragOver ? 'drag-over' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                type="file"
                ref={fileInputRef}
                accept="video/*"
                onChange={(e) => handleFileChange(e.target.files[0])}
                style={{ display: 'none' }}
              />
              <div className="uv-dropzone-icon">
                <i className="bi bi-camera-reels" />
              </div>
              <p className="uv-dropzone-primary">
                Drag and drop your video here, or <span className="uv-browse-text">browse</span>
              </p>
              <p className="uv-dropzone-sub">Supports MP4, MOV, WEBM or MKV (Max 2 mins duration, up to 700MB)</p>
            </div>
          ) : (
            <div className="uv-preview-container">
              <div className="uv-video-wrapper">
                <video src={previewUrl} controls className="uv-preview-video" />
                <button type="button" className="uv-remove-file-btn" onClick={handleRemoveFile}>
                  <i className="bi bi-trash" /> Remove
                </button>
              </div>
              <div className="uv-file-meta">
                <span className="uv-file-name"><i className="bi bi-file-earmark-play" /> {selectedFile.name}</span>
                <span className="uv-file-size">{videoDuration ? `${formatDuration(videoDuration)} • ` : ''}{formatFileSize(selectedFile.size)}</span>
              </div>
            </div>
          )}

          {/* Form Inputs */}
          <div className="uv-input-group">
            <label htmlFor="uv-title-input">
              Title <span className="text-danger">*</span>
            </label>
            <input
              id="uv-title-input"
              type="text"
              className="uv-form-control"
              placeholder="Give your video a catchy title..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="uv-input-group">
            <label htmlFor="uv-desc-input">Description</label>
            <textarea
              id="uv-desc-input"
              className="uv-form-control uv-textarea"
              rows={3}
              placeholder="What is your video about? Add context or details..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="uv-input-group">
            <label htmlFor="uv-tags-input">Tags</label>
            <input
              id="uv-tags-input"
              type="text"
              className="uv-form-control"
              placeholder="e.g. spanish, grammar, beginner (comma separated)"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
            />
          </div>

          {/* Footer Actions */}
          <div className="uv-modal-footer">
            <button
              type="button"
              className="uv-btn uv-btn-secondary"
              onClick={handleModalClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="uv-btn uv-btn-primary"
              disabled={!selectedFile || !title.trim()}
            >
              <i className="bi bi-upload me-1" /> Publish in Background
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
